import { useEffect, useRef } from 'react';
import { AnimationAction, AnimationMixer, Object3D } from 'three';
import type { CombatAnim } from './types';

/**
 * Map our combat-state names to clip names exported by various rig packs.
 * Order matters: KayKit Character Animations 1.1 names first (the current
 * default rig has ~110 baked actions from this pack), then Quaternius
 * "Ultimate Modular Men", then common Mixamo fallbacks.
 *
 * KayKit anim categories used here:
 *   - General: Idle_A/B, Hit_A/B, Death_A/B
 *   - MovementBasic/Advanced: Walking_*, Running_*
 *   - CombatMelee: Melee_1H_Attack_*, Melee_2H_Attack_*, Melee_Block_Hit
 *   - CombatRanged: Ranged_Bow_Release, Ranged_1H_Shoot, Ranged_Magic_Shoot
 */
const ANIM_CLIP: Record<CombatAnim, string[]> = {
  // Short canonical names FIRST (HK-rig uses these, matches via exact case-
  // insensitive equality before falling back to KayKit-style long names).
  idle:   ['idle', 'Idle_A', 'Idle_B', 'Melee_2H_Idle', 'Melee_Unarmed_Idle', 'Idle', 'Idle_Neutral'],
  walk:   ['walk', 'Walking_A', 'Walking_B', 'Walking_C', 'Running_A', 'Running_B', 'Walk', 'Run', 'Walking'],
  melee:  ['melee', 'Melee_1H_Attack_Slice_Diagonal', 'Melee_1H_Attack_Chop', 'Melee_1H_Attack_Slice_Horizontal', 'Melee_1H_Attack_Stab', 'Melee_2H_Attack_Chop', 'Melee_2H_Attack_Slice', 'Sword_Slash', 'Punch_Right'],
  ranged: ['ranged', 'Ranged_Bow_Release', 'Ranged_Bow_Release_Up', 'Ranged_1H_Shoot', 'Ranged_2H_Shoot', 'Bow_Shoot', 'Gun_Shoot', 'Shoot'],
  cast:   ['cast', 'Ranged_Magic_Shoot', 'Ranged_Magic_Spellcasting', 'Ranged_Magic_Summon', 'Ranged_Magic_Raise', 'Use_Item', 'Wave', 'Spellcast', 'Cast'],
  hurt:   ['hurt', 'Hit_A', 'Hit_B', 'Melee_Block_Hit', 'HitRecieve', 'HitRecieve_2', 'HitReact', 'Hurt'],
  die:    ['die', 'Death_A', 'Death_B', 'Death', 'Die'],
};

/**
 * Picks the first matching clip name from candidates that exists in the available list.
 * Returns null if none match.
 */
function pickClipName(available: string[], candidates: string[]): string | null {
  for (const c of candidates) if (available.includes(c)) return c;
  // Fallback: case-insensitive contains
  const low = available.map((a) => a.toLowerCase());
  for (const c of candidates) {
    const idx = low.findIndex((a) => a.includes(c.toLowerCase()));
    if (idx >= 0) return available[idx];
  }
  return null;
}

interface UseCharacterAnimationArgs {
  mixer: AnimationMixer | null;
  actions: Record<string, AnimationAction | null> | null;
  rootObject: Object3D | null;
  state: CombatAnim;
  onComplete?: () => void;
  /**
   * Bumping this number forces a re-trigger of the current state's animation
   * even if `state` itself didn't change. Used so multiple consecutive attacks
   * each play a fresh swing instead of leaving the first one running.
   */
  triggerKey?: number;
}

/**
 * Drives skeletal animation playback based on a high-level combat state.
 * Cross-fades between actions; one-shot anims (melee/ranged/cast/hurt/die)
 * trigger `onComplete` and, when not 'die', auto-return to idle.
 */
export function useCharacterAnimation({
  mixer, actions, state, onComplete, triggerKey = 0,
}: UseCharacterAnimationArgs) {
  const currentRef = useRef<AnimationAction | null>(null);

  useEffect(() => {
    if (!mixer || !actions) return;
    const available = Object.keys(actions);
    const clipName = pickClipName(available, ANIM_CLIP[state]);
    const next = clipName ? actions[clipName] : null;
    if (!next) return;

    const isOneShot = state === 'melee' || state === 'ranged' || state === 'cast' || state === 'hurt' || state === 'die';
    next.reset();
    next.setLoop(isOneShot ? 2200 : 2201, isOneShot ? 1 : Infinity); // LoopOnce=2200, LoopRepeat=2201
    next.clampWhenFinished = state === 'die';

    const prev = currentRef.current;
    // Always crossFade — even if previous is the same action; this gives a
    // smooth re-trigger when the same one-shot state fires twice (e.g. two
    // melee attacks in a row without bouncing through idle).
    if (prev) {
      next.crossFadeFrom(prev, 0.18, false);
    }
    next.fadeIn(0.18).play();
    currentRef.current = next;

    if (isOneShot && onComplete) {
      const onFinished = () => {
        mixer.removeEventListener('finished', onFinished);
        onComplete();
      };
      mixer.addEventListener('finished', onFinished);
      return () => mixer.removeEventListener('finished', onFinished);
    }
  }, [state, mixer, actions, onComplete, triggerKey]);
}
